/**
 * Printing a deal — every section, stage and photo choice, in one place
 * (`backend/app/reporting/deal_package.py`).
 *
 * The **dossier** is what this prints. It is the union of the two documents
 * that came before it — a Package (working print, stage-banded, photos
 * embeddable) and a Book (paginated institutional record) — assembled on the
 * Book's institutional furniture, with the Package's stage-banding and photo
 * embedding surviving as options inside it.
 *
 * `'package'` and `'book'` remain accepted by the server so a saved schedule
 * or an older client keeps rendering. Nothing new should ask for them.
 *
 * Section ids are shared across the two formats, so a section that both can
 * print is one selection rather than two — see SECTION_ALIASES server-side for
 * the two ids that were retired onto the sections that already printed them.
 */
import api from './api'
import { downloadDrillExport } from './dashboards'

/** One printable section, and whether this deal actually holds that data. */
/** The dossier, plus the two documents it replaced (server back-compat). */
export type DealDocumentKind = 'dossier' | 'package' | 'book'

/**
 * The dossier's two PDF forms (owner decision #2, 25 Sep 2026). 'full' is the
 * complete institutional record, set in Helios Paper Gold with the charts its
 * record supports; 'summary' is a 2-4 page Executive Summary built from the
 * same field-lineage data. Only meaningful with documentKind 'dossier' —
 * 'package'/'book' have no short form.
 */
export type DealPdfForm = 'summary' | 'full'

export interface DealPackageSection {
  id: string
  label: string
  /** The workflow band the section belongs to; null when it is stage-agnostic. */
  band: string | null
  /**
   * False when the deal has nothing to print in it. Offered but disabled rather
   * than hidden: a reviewer asking "where is the committee memo" deserves the
   * answer "this deal has none", not a menu that quietly omits it.
   */
  available: boolean
}

export interface DealPackagePhoto {
  id: string
  filename: string
  category: string | null
}

export interface DealPackageOptions {
  loanId: string
  propertyName: string
  currentStage: string
  documentKind?: DealDocumentKind
  sections: DealPackageSection[]
  stages: string[]
  photos: DealPackagePhoto[]
}

export interface DealPackageRequest {
  loanId: string
  documentKind?: DealDocumentKind
  /** 'full' (the default) when omitted — see DealPdfForm. */
  pdfForm?: DealPdfForm
  /** Empty means every section — the server's own contract, not a UI shortcut. */
  sections: string[]
  /** Empty means no stage filtering. */
  stages: string[]
  /**
   * The image documents the reader chose to print. On the dossier they lead the
   * cover (both PDF forms) and fill the Photographs section. A picture that is not
   * listed here is printed only if it is a standalone image filed under the category
   * "photos" or a picture inside the deal's offering memorandum or marketing PDF —
   * never because it is an image, since a deal's documents include identity scans and
   * payment instructions, and an image filed as marketing is printed only when chosen.
   */
  photoDocumentIds: string[]
}

function readBlobPrefix(blob: Blob, byteCount: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('The generated file could not be read.'))
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.readAsArrayBuffer(blob.slice(0, byteCount))
  })
}

/**
 * The name a `Content-Disposition` carries: the exact one from `filename*` (RFC 6266, percent-encoded UTF-8) when
 * the server sent it, else the quoted `filename` as a whole — a semicolon inside the quotes is part of the name —
 * else a bare token. `reports._content_disposition` sends a plain name as it is and a name the header cannot
 * carry as an ASCII stand-in with the exact name beside it. Null where the header names none.
 */
export function filenameFromDisposition(disposition: string | undefined): string | null {
  if (!disposition) return null
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition)
  if (star) {
    try {
      return decodeURIComponent(star[1].trim())
    } catch {
      // A malformed escape: fall back to the plain name beside it.
    }
  }
  const quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(disposition)
  if (quoted) return quoted[1].replace(/\\(.)/g, '$1')
  const bare = /filename\s*=\s*([^;]+)/i.exec(disposition)
  return bare ? bare[1].trim() : null
}

/** The server speaks snake_case here; it is not behind the camel-case transform. */
export const dealPackageService = {
  getOptions: (
    loanId: string,
    documentKind: DealDocumentKind = 'dossier',
  ): Promise<DealPackageOptions> =>
    api
      .get('/reports/deal-package/options', {
        params: { loan_id: loanId, document_kind: documentKind },
      })
      .then((r) => ({
        loanId: r.data.loan_id,
        propertyName: r.data.property_name,
        currentStage: r.data.current_stage,
        documentKind: r.data.document_kind ?? documentKind,
        sections: (r.data.sections ?? []) as DealPackageSection[],
        stages: (r.data.stages ?? []) as string[],
        photos: (r.data.photos ?? []) as DealPackagePhoto[],
      })),

  generate: async (
    request: DealPackageRequest,
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.post(
      '/reports/deal-package',
      {
        loan_id: request.loanId,
        document_kind: request.documentKind ?? 'dossier',
        pdf_form: request.pdfForm ?? 'full',
        sections: request.sections,
        stages: request.stages,
        photo_document_ids: request.photoDocumentIds,
      },
      { responseType: 'blob' },
    )
    const blob = response.data as Blob

    // An error page saved as .pdf is worse than a clear failure — the same
    // guard the drill export carries, for the same reason.
    const head = String.fromCharCode(...(await readBlobPrefix(blob, 5)))
    if (!head.startsWith('%PDF')) {
      throw new Error(`server returned ${blob.type || 'unknown content'} instead of a PDF`)
    }

    const disposition = response.headers['content-disposition'] as string | undefined
    const fallbackStem = request.pdfForm === 'summary' ? 'executive-summary' : 'deal-dossier'
    return {
      blob: blob.type ? blob : new Blob([blob], { type: 'application/pdf' }),
      filename: filenameFromDisposition(disposition) ?? `${fallbackStem}-${request.loanId}.pdf`,
    }
  },

  /** The per-deal Excel workbook: a Dashboard of native charts over the deal's
   *  figures, the Deal Summary sheet with the basis of each, and the whole deal
   *  record, one row per catalogue field with its machine-readable key, value
   *  state and basis (POST /reports/deal-template, same export permission as
   *  the dossier). */
  generateWorkbook: async (loanId: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.post(
      '/reports/deal-template',
      { loan_id: loanId },
      { responseType: 'blob' },
    )
    const blob = response.data as Blob
    const head = await readBlobPrefix(blob, 2)
    if (head[0] !== 0x50 || head[1] !== 0x4b) {
      throw new Error(`server returned ${blob.type || 'unknown content'} instead of a workbook`)
    }
    const disposition = response.headers['content-disposition'] as string | undefined
    return {
      blob: blob.type
        ? blob
        : new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      filename: filenameFromDisposition(disposition) ?? `deal-workbook-${loanId}.xlsx`,
    }
  },
}

export { downloadDrillExport as downloadDealPackage }
